/**
 * The body for POST /api/capture/processing-monitor/documents/:id/corrections.
 *
 * D1 (vault notes/capture/2026-09-26-review-time-profile-field-editing.md):
 * a CLICKED correction can teach the vendor profile where a field lives, so
 * its location evidence has to be exactly what the reviewer pointed at. The
 * evidence is sent only when the submitted value is still the clicked text
 * (trim-equal). Once the reviewer edits the value, or uses "type instead", the
 * clicked box no longer locates the value being submitted, so NO location is
 * sent and the correction is recorded as typed. The server stores what it is
 * given and never fills a typed correction's location in from elsewhere.
 *
 * @param {object|null} correctionModal  the Review page's correction state:
 *        { correctedValue, valueBbox, pageNumber, anchorText, anchorBbox,
 *          valuePosition, zone? }
 * @param {{field_key: string, corrected_value: string, typedInstead?: boolean}} submitted
 *        typedInstead: the reviewer pressed "type instead" in the modal. That
 *        abandons the click even if the retyped text matches it.
 */
export function isStillClickedValue(correctionModal, correctedValue, typedInstead = false) {
  if (typedInstead) return false;
  if (!correctionModal || !correctionModal.valueBbox) return false;
  const clicked = String(correctionModal.correctedValue ?? '').trim();
  return clicked !== '' && String(correctedValue ?? '').trim() === clicked;
}

export function buildCorrectionBody(correctionModal, { field_key, corrected_value, typedInstead = false }) {
  const body = { field_key, corrected_value };

  // The toolbar pencil's drawn zone is saved as an annotation for display. It
  // is not location evidence for learning and is unaffected by D1.
  if (correctionModal?.zone) {
    body.zone = {
      ...correctionModal.zone,
      fabricObject: correctionModal.zone.fabricObject,
    };
  }

  if (!isStillClickedValue(correctionModal, corrected_value, typedInstead)) {
    body.located_by = 'typed';
    return body;
  }

  body.located_by = 'click';
  body.value_bbox = correctionModal.valueBbox;
  // Sent explicitly even when null: "a click found no label" is itself the
  // evidence (region_only), and it must not be confused with "not sent".
  body.anchor_text_used = correctionModal.anchorText ?? null;
  body.anchor_bbox = correctionModal.anchorText ? (correctionModal.anchorBbox ?? null) : null;
  // The label is searched for on the value's own page only.
  body.anchor_page_number = correctionModal.anchorText && correctionModal.anchorBbox
    ? (correctionModal.pageNumber ?? null)
    : null;
  body.value_position = correctionModal.valuePosition ?? 'region_only';
  body.page_number = correctionModal.pageNumber ?? null;
  return body;
}
